import React from 'react'
import PropTypes from 'prop-types'
import { PTS_TO_DOLLAR } from '@common'
import './OrderSummaryTotals.css'

/**
 * OrderSummaryTotals Component
 * Displays order summary totals including subtotal, discounts, and final amount
 *
 * @param {number} originalAmount - Original order amount before discounts
 * @param {number} benefitDiscount - Staff benefit discount amount
 * @param {number} creditDiscount - Credit/points discount amount
 * @param {number} promoDiscount - Promo discount amount
 * @param {number} finalAmount - Final amount after all discounts
 * @param {string} baseFontSize - Font size for regular rows (e.g., '0.9rem', '14px', 'small')
 * @param {string} totalFontSize - Font size for total row (e.g., '1rem', '16px', 'large')
 */
export default function OrderSummaryTotals({
    originalAmount,
    benefitDiscount,
    creditDiscount,
    promoDiscount = 0,
    finalAmount,
    baseFontSize = '0.9rem',
    totalFontSize = '1rem',
}) {
    const pointsRedeemed = creditDiscount > 0 ? Math.round(creditDiscount * PTS_TO_DOLLAR) : 0

    // Convert predefined sizes to actual values
    const getFontSize = size => {
        const sizeMap = {
            small: '0.75rem',
            medium: '0.9rem',
            large: '1rem',
            xlarge: '1.25rem',
        }
        return sizeMap[size] || size
    }

    const baseSize = getFontSize(baseFontSize)
    const totalSize = getFontSize(totalFontSize)

    const rowStyle = { fontSize: baseSize }
    const totalRowStyle = { fontSize: totalSize }

    return (
        <div className="order-summary-totals-container">
            {originalAmount !== finalAmount && (
                <div className="order-summary-totals-row" style={rowStyle}>
                    <span className="order-summary-totals-label">Subtotal</span>
                    <span className="order-summary-totals-value">$ {originalAmount.toFixed(2)}</span>
                </div>
            )}
            {benefitDiscount > 0 && (
                <div className="order-summary-totals-row" style={rowStyle}>
                    <span className="order-summary-totals-label">Staff Benefit Discount</span>
                    <span className="order-summary-totals-value order-summary-totals-value-discount">
                        -$ {benefitDiscount.toFixed(2)}
                    </span>
                </div>
            )}
            {creditDiscount > 0 && (
                <div className="order-summary-totals-row" style={rowStyle}>
                    <span className="order-summary-totals-label">Points redeemed</span>
                    <span className="order-summary-totals-value order-summary-totals-value-discount">
                        -$ {creditDiscount.toFixed(2)} ({pointsRedeemed} pts)
                    </span>
                </div>
            )}
            {promoDiscount > 0 && (
                <div className="order-summary-totals-row" style={rowStyle}>
                    <span className="order-summary-totals-label">Promo Discount</span>
                    <span className="order-summary-totals-value order-summary-totals-value-discount">
                        -$ {promoDiscount.toFixed(2)}
                    </span>
                </div>
            )}
            <div className="order-summary-totals-row order-summary-totals-row-total" style={totalRowStyle}>
                <span className="order-summary-totals-label">Total</span>
                <span className="order-summary-totals-value">{finalAmount === 0 ? 'Free' : `$${finalAmount.toFixed(2)}`}</span>
            </div>
        </div>
    )
}

OrderSummaryTotals.propTypes = {
    originalAmount: PropTypes.number.isRequired,
    benefitDiscount: PropTypes.number.isRequired,
    creditDiscount: PropTypes.number.isRequired,
    promoDiscount: PropTypes.number,
    finalAmount: PropTypes.number.isRequired,
    baseFontSize: PropTypes.string,
    totalFontSize: PropTypes.string,
}
